import * as d3 from 'd3';
import './GraphTooltipController.scss';

interface GraphTooltipOptions {
  /** A unique id associated with the tooltip. Only needs to be provided if multiple tooltips are created on the same SVG element. */
  id: string;
}

export interface SVGViewBox {
  xMin: number;
  yMin: number;
  width: number;
  height: number;
}

const defaultOptions: GraphTooltipOptions = {
  id: 'tooltip',
};

// TODO: Remove timestamp text somewhere else besides here
/**
 * A utility for rendering a tooltip within an SVG document.
 */
export class GraphTooltipController {
  private readonly svg: SVGGraphicsElement;
  private readonly root: d3.Selection<SVGGraphicsElement, unknown, null, unknown>;
  private readonly opts: GraphTooltipOptions;
  private rect: Pick<DOMRect, 'x' | 'y' | 'width' | 'height'> = { width: 0, height: 0, x: 0, y: 0 };

  /** The lines of text to display. */
  private lines: string[] = [];
  /** The x-coordinate to place the tooltip. */
  private x: number = 0;
  /** The y-coordinate to place the tooltip. */
  private y: number = 0;

  private get margin(): number {
    return this.rect.width / 100;
  }

  private get rx(): number {
    return Math.max(this.rect.width, this.rect.height) / 100;
  }

  private get gId(): string {
    return `${this.opts.id}-g`;
  }

  private get textId(): string {
    return `${this.opts.id}-text`;
  }

  private get rectId(): string {
    return `${this.opts.id}-rect`;
  }

  /**
   * Creates a new tooltip associated with an SVG document.
   * @param root The root svg element in which the tooltip will be rendered.
   * @param opts Addition options used to customize the tooltip.
   */
  constructor(root: SVGGraphicsElement, opts: Partial<GraphTooltipOptions> = {}) {
    this.svg = root;
    this.root = d3.select(root);
    this.opts = { ...defaultOptions, ...opts };

    this.onParentLayoutChanged();
    this.createTooltip();
  }

  /**
   * Recalculates the acceptable bounds for tooltip placement. Should be called by consumers whenever the size of the SVG changes in the DOM.
   */
  public readonly onParentLayoutChanged = () => {
    if (this.isSVGSVGElement(this.svg)) {
      this.rect = {
        x: this.svg.viewBox?.baseVal?.x ?? 0,
        y: this.svg.viewBox?.baseVal?.y ?? 0,
        width: this.svg.viewBox?.baseVal?.width ?? this.svg.width ?? this.svg.getBoundingClientRect().width,
        height: this.svg.viewBox?.baseVal?.height ?? this.svg.height ?? this.svg.getBoundingClientRect().height,
      };
    } else {
      this.rect = this.svg.getBBox();
    }

    this.render();
  };

  /**
   * Sets whether or not the tooltip is visible.
   * @param visible Whether or not the tooltip is visible.
   */
  public readonly setVisible = (visible: boolean) => {
    this.root.select(`[data-id=${this.gId}]`).classed('graph-tooltip-hidden', !visible);
  };

  /**
   * Sets the text to display in the tooltip. Coordinates should be relative to the element provided
   * to the constructor as the `root` param. You may use `d3.pointer(event, root)` to get these coordinates.
   * @param lines The lines of text to display.
   * @param x The x-coordinate to place the tooltip.
   * @param y The y-coordinate to place the tooltip.
   * @param transition The name of the transition group to associate with the update.
   */
  public readonly setText = (lines: string[], x: number, y: number, transition?: string) => {
    this.lines = lines;
    this.x = x;
    this.y = y;

    this.render(transition);
  };

  private readonly render = (transition?: string) => {
    if (!this.lines || this.lines.length === 0) {
      return;
    }

    const isRight = this.x - this.rect.x > this.rect.width / 2;
    const isBottom = this.y - this.rect.y > this.rect.height / 2;
    const padding = this.getPadding();

    const text = this.root.select<SVGTextElement>(`[data-id=${this.textId}]`);
    if (!text.node()) return;
    text
      .attr('transform', `translate(${padding},${padding})`)
      .selectAll('tspan')
      .data(this.lines)
      .join('tspan')
      .text(d => d?.trim() ?? '')
      .attr('font-size', this.getFontSize())
      .attr('dy', '1em')
      .attr('x', 0);

    const textBoxRect = text.node()!.getBBox();
    this.root
      .select(`[data-id=${this.rectId}]`)
      .attr('width', textBoxRect.width + padding * 2)
      .attr('height', textBoxRect.height + padding * 2)
      .attr('rx', this.rx);

    // Right align text
    if (isRight) {
      text.selectAll<SVGTSpanElement, string>('tspan').attr('x', function (this, d) {
        const tspanWidth = this.getComputedTextLength();
        return textBoxRect.width - tspanWidth;
      });
    }

    const g = this.root.select<SVGGElement>(`[data-id=${this.gId}]`);
    const gRect = g.node()!.getBBox();
    const effectiveX = isRight ? this.x - this.margin - gRect.width : this.x + this.margin;
    const clampedX = Math.max(this.rect.x, Math.min(this.rect.x + this.rect.width - gRect.width, effectiveX));
    const effectiveY = isBottom ? this.y - this.margin - gRect.height : this.y + this.margin;
    const clampedY = Math.max(this.rect.y, Math.min(this.rect.y + this.rect.height - gRect.height, effectiveY));
    let selection = this.root.select(`[data-id=${this.gId}]`);
    if (transition) selection = selection.transition(transition) as any;
    selection.attr('transform', `translate(${clampedX}, ${clampedY})`);
  };

  private readonly isSVGSVGElement = (el: SVGGraphicsElement): el is SVGSVGElement => {
    return el.tagName === 'svg';
  };

  private readonly createTooltip = () => {
    this.root.selectAll(`[data-id=${this.gId}]`).remove();
    const g = this.root.append('g').attr('data-id', this.gId).classed('graph-tooltip', true);
    const padding = this.getPadding();

    g.append('rect')
      .attr('data-id', this.rectId)
      .classed('graph-tooltip-rect', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 0)
      .attr('height', 0)
      .attr('rx', 0);
    g.append('text')
      .attr('data-id', this.textId)
      .attr('transform', `translate(${padding},${padding})`)
      .classed('graph-tooltip-text', true);
  };

  /** Returns the SVG unit which corresponds to 1 pixel on the current screen. */
  private getPixelUnit() {
    const pixelWidth = this.svg.getBoundingClientRect().width;
    const viewBoxWidth = this.rect.width;
    const result = viewBoxWidth / pixelWidth;
    if (isNaN(result) || result === Number.POSITIVE_INFINITY || result === Number.NEGATIVE_INFINITY) return 1;
    return result;
  }

  private getFontSize(): number {
    const ratio = this.getPixelUnit();
    return ratio * 14;
  }

  private getPadding(): number {
    const ratio = this.getPixelUnit();
    return ratio * 8;
  }
}
